import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { ProductTypeInput } from '@barboxx/admin-components';
import { CREATE_ALCOHOL_SUBTYPE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductSubtype } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateProductSubtypeDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<ProductSubtype.CreateRequest>({
        resolver: yupResolver(CREATE_ALCOHOL_SUBTYPE_SCHEMA),
        defaultValues: {},
    });

    const mutateCreateProductSubtype = useMutation({
        mutationKey: ['productSubtype'],
        mutationFn: (data: ProductSubtype.CreateRequest) =>
            Api.Products.Subtypes.postProductSubtype(data),
    });

    const onSubmit = async (values: ProductSubtype.CreateRequest) => {
        return mutateCreateProductSubtype
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating product!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Alcohol Subtype
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Aging Requirements"
                        name="agingRequirements"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Regional Requirements"
                        name="regionalRequirements"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Other Specifications"
                        name="otherSpecifications"
                        fullWidth
                    />
                    <ProductTypeInput />
                    <CheckboxElement name="isAlcoholic" label="Is Alcoholic" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
